import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiOutlineSearch, HiOutlineUsers, HiPlus } from "react-icons/hi";
import HttpService from "../../services/http";
import UserAvatar from "../atoms/UserAvatar";
import { CustomButton } from "../atoms/CustomButton";
import { EmptyState } from "../atoms/EmptyState";
import { Spinner } from "../atoms/Spinner";
import { StatusBadge } from "../atoms/StatusBadge";

export const PERSON_SEARCH_MIN_LENGTH = 5;

export interface FoundPerson {
  user_identification: string;
  user_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  avatar: string;
  user_role: string;
  user_type: string;
  user_account_status: string;
}

const toFoundPerson = (user: any): FoundPerson => ({
  user_identification: user.user_identification,
  user_name: user.user_user_name || "",
  first_name: user.user_first_name || "",
  last_name: user.user_last_name || "",
  email: user.user_email || "",
  phone: user.user_phone || "",
  avatar: user.user_avatar || "",
  user_role: user.user_role || "",
  user_type: user.user_type || "",
  user_account_status: user.user_account_status || "",
});

type Outcome = { people: FoundPerson[]; tooMany: boolean } | null;

/** The search state, kept by the caller so that it can outlive the window that shows it. */
export const usePersonSearch = (customerId: string, entityId: string) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [isSearching, setIsSearching] = useState(false);
  const lastRequest = useRef(0);

  const search = async () => {
    const wanted = query.trim();
    if (wanted.length < PERSON_SEARCH_MIN_LENGTH) {
      toast.error(t("personSearch.minCharacters"));
      return;
    }
    const request = ++lastRequest.current;
    setIsSearching(true);
    try {
      const response = await HttpService.get(
        `/${customerId}/${entityId}/user/search?query=${encodeURIComponent(
          wanted
        )}`
      );
      if (request !== lastRequest.current) return;
      setOutcome(
        response?.data?.code === "TOO_MANY_RESULTS"
          ? { people: [], tooMany: true }
          : {
              people: (response?.data?.element?.users || []).map(toFoundPerson),
              tooMany: false,
            }
      );
    } catch {
      if (request !== lastRequest.current) return;
      toast.error(t("personSearch.searchError"));
      setOutcome(null);
    } finally {
      if (request === lastRequest.current) setIsSearching(false);
    }
  };

  const reset = useCallback(() => {
    lastRequest.current += 1;
    setQuery("");
    setOutcome(null);
    setIsSearching(false);
  }, []);

  return { query, setQuery, outcome, isSearching, search, reset };
};

export type PersonSearchState = ReturnType<typeof usePersonSearch>;

interface PersonSearchProps {
  state: PersonSearchState;
  renderAction?: (person: FoundPerson) => React.ReactNode;
  gridClassName?: string;
  idle?: React.ReactNode;
}

const SearchField: React.FC<{
  state: PersonSearchState;
  compact?: boolean;
}> = ({ state, compact = false }) => {
  const { t } = useTranslation();
  const { query, setQuery, isSearching, search } = state;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    void search();
  };

  return (
    <div className={compact ? "" : "mb-4 sm:mb-6"}>
      <p
        className={
          compact
            ? "mb-2 text-sm text-gray-600"
            : "text-gray-600 text-sm mb-3 sm:mb-4"
        }
      >
        {t("personSearch.description")}
      </p>

      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label={t("personSearch.placeholder") as string}
            placeholder={t("personSearch.placeholder") as string}
            className="block w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
          />
        </div>
        <CustomButton
          variant="primary"
          onClick={() => void search()}
          disabled={
            isSearching || query.trim().length < PERSON_SEARCH_MIN_LENGTH
          }
          isLoading={isSearching}
          title={t("common.search") as string}
        />
      </div>
    </div>
  );
};

const DEFAULT_IDLE = (
  <div className="text-center py-8">
    <HiOutlineSearch size={48} className="mx-auto text-gray-400 mb-3" />
  </div>
);

/** The people of the entity whose name, e-mail or identifier holds the query (`GET /user/search`, `SEARCH` V). */
const PersonSearch: React.FC<PersonSearchProps> = ({
  state,
  renderAction,
  gridClassName = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4",
  idle = DEFAULT_IDLE,
}) => {
  const { t } = useTranslation();
  const { query, outcome, isSearching } = state;

  const roleLabel = (role?: string): string => {
    if (!role) return t("personSearch.roles.unknown");
    switch (role.toLowerCase()) {
      case "owner":
        return t("personSearch.roles.owner");
      case "admin":
        return t("personSearch.roles.admin");
      case "default":
        return t("personSearch.roles.user");
      default:
        return role;
    }
  };

  const typeLabel = (type?: string): string => {
    if (!type) return t("personSearch.types.unknown");
    switch (type.toLowerCase()) {
      case "admin":
        return t("personSearch.types.admin");
      case "default":
        return t("personSearch.types.standard");
      default:
        return type;
    }
  };

  let results: React.ReactNode = null;
  if (!query.trim() || outcome === null) {
    results = isSearching ? null : idle;
  } else if (outcome.tooMany) {
    results = (
      <EmptyState
        icon={<HiOutlineUsers size={24} />}
        title={t("dashboard.searchPage.tooMany.title")}
        description={t("dashboard.searchPage.tooMany.description")}
      />
    );
  } else if (outcome.people.length === 0) {
    results = (
      <EmptyState
        icon={<HiOutlineUsers size={24} />}
        title={t("personSearch.noResults")}
      />
    );
  } else {
    results = (
      <div>
        <div className="mb-3 sm:mb-4 text-sm text-gray-600">
          {t("personSearch.found", { count: outcome.people.length })}
        </div>
        <div className={gridClassName}>
          {outcome.people.map((person) => {
            const action = renderAction?.(person);
            const fullName = `${person.first_name} ${person.last_name}`;
            return (
              <div
                key={person.user_identification}
                className="h-full border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-blue-300 transition-colors overflow-hidden"
              >
                <div className="h-full flex flex-col gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="relative flex-shrink-0">
                      <UserAvatar
                        avatar={person.avatar || undefined}
                        firstName={person.first_name}
                        lastName={person.last_name}
                        sizeClass="w-10 h-10"
                      />
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                          person.user_account_status?.toLowerCase() === "ok"
                            ? "bg-green-500"
                            : "bg-amber-500"
                        }`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4
                        className="font-medium text-gray-900 truncate"
                        title={fullName}
                      >
                        {person.first_name} {person.last_name}
                      </h4>
                      <p
                        className="text-sm text-gray-600 truncate"
                        title={person.email}
                      >
                        {person.email}
                      </p>
                      {person.phone && (
                        <p
                          className="text-sm text-gray-500 truncate"
                          title={person.phone}
                        >
                          {person.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 font-medium">
                        {t("personSearch.role")}
                      </span>
                      <StatusBadge color="gray" dot size="sm">
                        {roleLabel(person.user_role)}
                      </StatusBadge>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 font-medium">
                        {t("personSearch.type")}
                      </span>
                      <StatusBadge color="gray" dot size="sm">
                        {typeLabel(person.user_type)}
                      </StatusBadge>
                    </div>
                  </div>

                  {action && (
                    <div className="mt-auto flex justify-end">{action}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      <SearchField state={state} />

      {results}

      {isSearching && (
        <div className="text-center py-4">
          <Spinner size="md" />
        </div>
      )}
    </>
  );
};

interface PersonSearchListProps {
  state: PersonSearchState;
  onPick: (person: FoundPerson) => void;
  /** Read before each person's name, e.g. "Add". */
  pickLabel: string;
  /** Why a person cannot be picked, shown on their row; null when they can. */
  unavailable?: (person: FoundPerson) => string | null;
  disabled?: boolean;
}

const LIST_NOTE = "px-3 py-2 text-sm text-gray-600";

/** The same search, its results as a compact list under the field: one click picks a person. */
export const PersonSearchList: React.FC<PersonSearchListProps> = ({
  state,
  onPick,
  pickLabel,
  unavailable,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { query, outcome, isSearching } = state;

  let rows: React.ReactNode = null;
  if (isSearching || !query.trim() || outcome === null) {
    rows = null;
  } else if (outcome.tooMany) {
    rows = (
      <li className={LIST_NOTE}>
        <span className="block font-medium text-gray-900">
          {t("dashboard.searchPage.tooMany.title")}
        </span>
        {t("dashboard.searchPage.tooMany.description")}
      </li>
    );
  } else if (outcome.people.length === 0) {
    rows = <li className={LIST_NOTE}>{t("personSearch.noResults")}</li>;
  } else {
    rows = outcome.people.map((person) => {
      const reason = unavailable?.(person) ?? null;
      const name =
        `${person.first_name} ${person.last_name}`.trim() || person.email;
      return (
        <li key={person.user_identification}>
          <button
            type="button"
            disabled={disabled || reason !== null}
            onClick={() => onPick(person)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left enabled:hover:bg-blue-50 disabled:cursor-default"
          >
            <span aria-hidden="true" className="shrink-0">
              <UserAvatar
                avatar={person.avatar || undefined}
                firstName={person.first_name}
                lastName={person.last_name}
                email={person.email}
                sizeClass="w-8 h-8"
                textClass="text-xs"
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="sr-only">{pickLabel} </span>
              <span className="block truncate text-sm text-gray-900">
                {name}
              </span>{" "}
              {person.email && name !== person.email && (
                <span className="block truncate text-xs text-gray-500">
                  {person.email}
                </span>
              )}
            </span>
            {reason !== null ? (
              <span className="shrink-0 text-xs text-gray-500">{reason}</span>
            ) : (
              !disabled && (
                <HiPlus
                  size={16}
                  aria-hidden="true"
                  className="shrink-0 text-blue-600"
                />
              )
            )}
          </button>
        </li>
      );
    });
  }

  return (
    <>
      <SearchField state={state} compact />
      {rows && (
        <ul className="mt-2 max-h-52 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 shadow-sm">
          {rows}
        </ul>
      )}
    </>
  );
};

export default PersonSearch;
